import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Loader2,
  RefreshCcw,
  Plus,
  Trash2,
  Save,
  Search,
  Pencil,
  RotateCcw,
  Upload,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const empty = { key: "", label: "", icon: "pin", types: "", keyword: "", search_type: "type" };

// Very small CSV parser (handles quoted fields with commas & newlines).
function parseCsv(text) {
  const rows = [];
  let cur = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
      } else field += c;
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ",") { cur.push(field); field = ""; }
      else if (c === "\n" || c === "\r") {
        if (field !== "" || cur.length) { cur.push(field); rows.push(cur); cur = []; field = ""; }
        if (c === "\r" && text[i + 1] === "\n") i++;
      } else field += c;
    }
  }
  if (field !== "" || cur.length) { cur.push(field); rows.push(cur); }
  return rows.filter((r) => r.length > 0 && r.some((c) => (c || "").trim() !== ""));
}

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState({ open: false, mode: "create", data: empty });
  const [importDlg, setImportDlg] = useState({
    open: false,
    file: null,
    rows: [],
    mode: "merge",
    result: null,
    running: false,
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/categories");
      setCats(data);
    } catch (e) {
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = cats.filter(
    (c) =>
      !q ||
      c.label.toLowerCase().includes(q.toLowerCase()) ||
      c.key.toLowerCase().includes(q.toLowerCase())
  );

  const openCreate = () =>
    setDlg({ open: true, mode: "create", data: { ...empty } });

  const openEdit = (c) =>
    setDlg({
      open: true,
      mode: "edit",
      data: {
        _originalKey: c.key,
        key: c.key,
        label: c.label,
        icon: c.icon || "pin",
        types: (c.types || []).join(", "),
        keyword: c.keyword || "",
        search_type: c.search_type || "type",
        order: c.order,
      },
    });

  const save = async () => {
    const d = dlg.data;
    const isKeyword = d.search_type === "keyword";
    if (!d.key || !d.label) {
      toast.error("Key and label are required");
      return;
    }
    if (isKeyword && !d.keyword.trim()) {
      toast.error("Keyword is required for keyword search");
      return;
    }
    if (!isKeyword && !d.types.trim()) {
      toast.error("Types are required for type-based search");
      return;
    }
    const payload = {
      key: d.key.trim(),
      label: d.label.trim(),
      icon: d.icon.trim() || "pin",
      types: d.types.split(",").map((s) => s.trim()).filter(Boolean),
      keyword: d.keyword.trim() || null,
      search_type: d.search_type,
      order: d.order,
    };
    try {
      if (dlg.mode === "create") {
        await api.post("/categories", payload);
        toast.success("Category created");
      } else {
        await api.put(`/categories/${d._originalKey}`, payload);
        toast.success("Category updated");
      }
      setDlg({ open: false, mode: "create", data: empty });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (key) => {
    if (!confirm(`Delete category '${key}'?`)) return;
    try {
      await api.delete(`/categories/${key}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const reset = async () => {
    if (
      !confirm(
        "Reset ALL categories back to the 52 defaults? This will delete any custom categories."
      )
    )
      return;
    try {
      await api.post("/categories/reset");
      toast.success("Categories reset");
      load();
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  const onCsvFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    const rows = parseCsv(text);
    if (!rows.length) {
      toast.error("CSV is empty");
      return;
    }
    // Detect header row
    const first = rows[0].map((c) => (c || "").trim().toLowerCase());
    const hasHeader =
      first.includes("key") && first.includes("label") && first.includes("types");
    let idx = { key: 0, label: 1, icon: 2, types: 3 };
    let body = rows;
    if (hasHeader) {
      idx = {
        key: first.indexOf("key"),
        label: first.indexOf("label"),
        icon: first.indexOf("icon"),
        types: first.indexOf("types"),
      };
      body = rows.slice(1);
    }
    const parsed = body.map((r) => ({
      key: (r[idx.key] || "").trim(),
      label: (r[idx.label] || "").trim(),
      icon: (r[idx.icon] || "pin").trim() || "pin",
      types: (r[idx.types] || "").trim(),
    }));
    setImportDlg((s) => ({ ...s, file, rows: parsed, result: null }));
  };

  const runImport = async () => {
    setImportDlg((s) => ({ ...s, running: true }));
    try {
      const { data } = await api.post("/categories/import", {
        rows: importDlg.rows,
        mode: importDlg.mode,
      });
      setImportDlg((s) => ({ ...s, result: data, running: false }));
      toast.success(
        `Imported: ${data.inserted} added, ${data.updated} updated, ${data.skipped} skipped`
      );
      load();
    } catch (e) {
      setImportDlg((s) => ({ ...s, running: false }));
      toast.error(e?.response?.data?.detail || "Import failed");
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-8 animate-in-up flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
            Catalogue
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">
            Categories
          </h1>
          <p className="text-sm text-muted-foreground">
            Add, edit or delete the place categories used by the NearMe mobile app.{" "}
            <span className="font-semibold text-foreground">{cats.length}</span> total.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={load}
            data-testid="cats-refresh-btn"
            className="h-10"
          >
            <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button
            variant="outline"
            onClick={reset}
            data-testid="cats-reset-btn"
            className="h-10"
          >
            <RotateCcw className="w-4 h-4 mr-2" /> Reset defaults
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              setImportDlg({
                open: true,
                file: null,
                rows: [],
                mode: "merge",
                result: null,
                running: false,
              })
            }
            data-testid="cats-import-btn"
            className="h-10"
          >
            <Upload className="w-4 h-4 mr-2" /> Import CSV
          </Button>
          <Button onClick={openCreate} data-testid="cats-add-btn" className="h-10">
            <Plus className="w-4 h-4 mr-2" /> New category
          </Button>
        </div>
      </div>

      <div className="mb-6 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search categories"
          className="pl-9 h-11"
          data-testid="categories-search-input"
        />
      </div>

      {loading ? (
        <div className="text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 stagger"
          data-testid="categories-grid"
        >
          {filtered.map((c) => (
            <div
              key={c.key}
              data-testid={`category-${c.key}`}
              className="group bg-card border border-border rounded-xl p-4 hover:border-primary hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="text-base font-bold truncate">{c.label}</div>
                  <div className="text-xs text-muted-foreground mono truncate">
                    {c.key}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEdit(c)}
                    data-testid={`cat-edit-${c.key}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => del(c.key)}
                    data-testid={`cat-delete-${c.key}`}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(c.types || []).map((t) => (
                  <span
                    key={t}
                    className="mono text-[10px] px-1.5 py-0.5 rounded bg-secondary text-secondary-foreground"
                  >
                    {t}
                  </span>
                ))}
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-widest text-muted-foreground">
                icon: <span className="mono">{c.icon}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog
        open={dlg.open}
        onOpenChange={(o) => setDlg((s) => ({ ...s, open: o }))}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {dlg.mode === "create" ? "New category" : "Edit category"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <F
              label="Key"
              hint="Unique lowercase identifier, e.g. 'coffee'"
              value={dlg.data.key}
              onChange={(v) =>
                setDlg((s) => ({ ...s, data: { ...s.data, key: v } }))
              }
              testId="cat-key-input"
              mono
            />
            <F
              label="Label"
              hint="Shown to the user, e.g. 'Coffee Shop'"
              value={dlg.data.label}
              onChange={(v) =>
                setDlg((s) => ({ ...s, data: { ...s.data, label: v } }))
              }
              testId="cat-label-input"
            />
            <F
              label="Icon"
              hint="Ionicons name (Expo app), e.g. 'cafe'"
              value={dlg.data.icon}
              onChange={(v) =>
                setDlg((s) => ({ ...s, data: { ...s.data, icon: v } }))
              }
              testId="cat-icon-input"
              mono
            />

            {/* Search-strategy selector */}
            <div className="col-span-full">
              <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                Search strategy
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: "type", label: "By place type", hint: "Google places:searchNearby" },
                  { v: "keyword", label: "By keyword", hint: "Google places:searchText, falls back to type if 0 results" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    data-testid={`cat-search-type-${opt.v}`}
                    onClick={() =>
                      setDlg((s) => ({
                        ...s,
                        data: { ...s.data, search_type: opt.v },
                      }))
                    }
                    className={`text-left rounded-xl border p-3 transition-all ${
                      dlg.data.search_type === opt.v
                        ? "border-primary bg-primary/5 shadow-sm"
                        : "border-border bg-card hover:border-muted-foreground"
                    }`}
                  >
                    <div className="text-sm font-bold">{opt.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {opt.hint}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {dlg.data.search_type === "keyword" && (
              <F
                label="Search keyword"
                hint="Free-text query sent to Google (e.g. 'Aadhaar Seva Kendra')"
                value={dlg.data.keyword}
                onChange={(v) =>
                  setDlg((s) => ({ ...s, data: { ...s.data, keyword: v } }))
                }
                testId="cat-keyword-input"
              />
            )}
            <F
              label={
                dlg.data.search_type === "keyword"
                  ? "Fallback place types (comma-separated)"
                  : "Google Place types (comma-separated)"
              }
              hint={
                dlg.data.search_type === "keyword"
                  ? "Used only when keyword returns 0 results, e.g. 'government_office'"
                  : "e.g. coffee_shop, cafe"
              }
              value={dlg.data.types}
              onChange={(v) =>
                setDlg((s) => ({ ...s, data: { ...s.data, types: v } }))
              }
              testId="cat-types-input"
              mono
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDlg((s) => ({ ...s, open: false }))}
              data-testid="cat-cancel-btn"
            >
              Cancel
            </Button>
            <Button onClick={save} data-testid="cat-save-btn">
              <Save className="w-4 h-4 mr-2" />{" "}
              {dlg.mode === "create" ? "Create" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog
        open={importDlg.open}
        onOpenChange={(o) => setImportDlg((s) => ({ ...s, open: o }))}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import categories from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-xs text-muted-foreground">
              CSV must contain columns:{" "}
              <span className="mono">key,label,icon,types</span>. Types can be
              comma-separated (wrap in quotes) or a single value. Header row
              recognised automatically.
            </div>

            <div className="border-2 border-dashed border-border rounded-xl p-6 text-center">
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => onCsvFile(e.target.files?.[0])}
                data-testid="csv-file-input"
                className="hidden"
                id="csv-file-input"
              />
              <label
                htmlFor="csv-file-input"
                className="cursor-pointer inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
              >
                <FileText className="w-4 h-4" />
                {importDlg.file ? importDlg.file.name : "Choose a CSV file…"}
              </label>
              {importDlg.rows.length > 0 && (
                <div className="text-xs text-muted-foreground mt-2">
                  {importDlg.rows.length} row
                  {importDlg.rows.length === 1 ? "" : "s"} parsed
                </div>
              )}
            </div>

            {importDlg.rows.length > 0 && (
              <>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Import mode
                  </Label>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {["merge", "upsert", "replace"].map((m) => (
                      <button
                        key={m}
                        onClick={() => setImportDlg((s) => ({ ...s, mode: m }))}
                        data-testid={`csv-mode-${m}`}
                        className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${
                          importDlg.mode === m
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-card border-border text-muted-foreground hover:border-primary"
                        }`}
                      >
                        {m === "merge"
                          ? "Merge (skip existing)"
                          : m === "upsert"
                          ? "Upsert (update existing)"
                          : "Replace (wipe all)"}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="max-h-48 overflow-y-auto border border-border rounded-lg text-xs">
                  <table className="w-full">
                    <thead className="bg-secondary text-secondary-foreground sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">Key</th>
                        <th className="text-left px-3 py-2 font-semibold">Label</th>
                        <th className="text-left px-3 py-2 font-semibold">Types</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importDlg.rows.slice(0, 100).map((r, i) => (
                        <tr
                          key={`${r.key || "row"}-${i}`}
                          className="border-t border-border"
                        >
                          <td className="px-3 py-1.5 mono truncate max-w-[140px]">
                            {r.key}
                          </td>
                          <td className="px-3 py-1.5">{r.label}</td>
                          <td className="px-3 py-1.5 mono text-muted-foreground truncate max-w-[200px]">
                            {r.types}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importDlg.rows.length > 100 && (
                    <div className="p-2 text-center text-muted-foreground">
                      … and {importDlg.rows.length - 100} more
                    </div>
                  )}
                </div>
              </>
            )}

            {importDlg.result && (
              <div
                className="bg-accent text-accent-foreground rounded-lg p-4 text-sm"
                data-testid="csv-import-result"
              >
                <div className="font-bold mb-1">Import complete</div>
                <div className="text-xs">
                  Inserted: {importDlg.result.inserted} · Updated:{" "}
                  {importDlg.result.updated} · Skipped: {importDlg.result.skipped}
                  {importDlg.result.errors?.length > 0 && (
                    <> · Errors: {importDlg.result.errors.length}</>
                  )}{" "}
                  · Total now: {importDlg.result.total_categories}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setImportDlg((s) => ({ ...s, open: false }))}
              data-testid="csv-cancel-btn"
            >
              Close
            </Button>
            <Button
              onClick={runImport}
              disabled={importDlg.running || importDlg.rows.length === 0}
              data-testid="csv-run-import-btn"
            >
              {importDlg.running ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Import {importDlg.rows.length > 0 ? `${importDlg.rows.length} rows` : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, hint, value, onChange, testId, mono }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {label}
    </Label>
    <Input
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className={`mt-1.5 h-11 ${mono ? "mono text-sm" : ""}`}
    />
    {hint && <p className="text-xs text-muted-foreground mt-1.5">{hint}</p>}
  </div>
);
